import { createAsyncThunk, isFulfilled } from '@reduxjs/toolkit'
import { getSlot, SlotType } from 'api/v1/slot'
import {
  CreateOrderBody,
  CreateSpotOrderResponse,
  postCreateSpotOrder,
  TradingFeeRates,
} from 'api/v1/spot'
import toastify from 'components/Toastify'
import { SUB_ACCOUNT_ID } from 'config'
import { RootState, store } from 'store'
import { updateActionState } from 'store/app/actions'
import { TradeType } from 'store/market/types'
import {
  OrderDirection,
  OrderModeDetail,
  OrderModeNames,
} from 'store/spot/types'
import { Ether, L2AccountId, SymbolName, TokenId } from 'types'
import { calculateMarketPriceForStopMarket } from 'utils/calculateMarketPriceForStopMarket'
import { getStopOrderTriggerDirection } from 'utils/getStopOrderTriggerDirection'
import { bn, e2w, isZero, w2e } from 'utils/number'
import { signOrder } from 'utils/signer/transactions/order'
import { sdk } from 'utils/signer/utils'
import { getMarketPriceConfigAction } from '../../market/actions/getMarketPriceConfigActions'
import i18n from '../../../i18n'
import { updateCreateOrderOption } from '../actions'
import { formatUnits } from 'ethers/lib/utils'
import { TifType } from 'store/settings/types'
import { defaultTif } from 'store/settings/reducer'

export interface BaseCreateOrderPayload {
  orderMode: OrderModeDetail
  orderDirection: OrderDirection
  l2AccountId: L2AccountId
  symbol: SymbolName
  price: Ether
  stopPrice: Ether
  quoteScale: number
  amount: Ether // packed token amount, e.g. '1.233200000'
  feeRates: TradingFeeRates | undefined
}

export interface SpotCreateOrderPayload extends BaseCreateOrderPayload {
  baseTokenId: TokenId
  quoteTokenId: TokenId
}

export const createSpotOrderAction = createAsyncThunk<
  CreateSpotOrderResponse,
  SpotCreateOrderPayload,
  { state: RootState }
>('spot/createOrderAction', async (sourcePayload, { getState, dispatch }) => {
  try {
    dispatch(updateActionState({ action: 'ordering', state: true }))

    const { orderMode, stopPrice, quoteScale } = sourcePayload

    const state = getState()
    const { signer } = state.link
    const { postOnly = false } = state.spot
    const { tif = defaultTif } = state.settings ?? {}

    // Do not arbitrarily modify the following fill order,
    // because fillZkSignature depends on the result data after fillSlotIdAndNonce.
    let payload = initialOrderPayload(sourcePayload)
    payload = fillStopLimitOrder(payload, orderMode, stopPrice)
    payload = await fillStopMarketOrder(
      payload,
      orderMode,
      stopPrice,
      quoteScale,
      TradeType.Spot,
    )
    payload = await fillSlotIdAndNonce(payload, SlotType.Spot)
    payload = fillHidden(payload, orderMode)
    payload = await fillSpotZkSignature(payload, sourcePayload, signer!)
    payload = fillTriggerDirection(payload, orderMode)
    payload = convertFeeRateToFloat(payload, sourcePayload)
    payload = fillOrderTIF(payload, orderMode, tif[TradeType.Spot])
    payload = fillOrderPostOnly(payload, orderMode, postOnly)

    const response = await postCreateSpotOrder(0, payload)

    toastify.success(i18n.t('order-submitted'))

    // Clear amount input
    dispatch(
      updateCreateOrderOption({
        option: 'baseCurrencyAmount',
        value: '',
      }),
    )
    dispatch(
      updateCreateOrderOption({
        option: 'quoteCurrencyAmount',
        value: '',
      }),
    )

    return response.data
  } catch (e) {
    toastify.error(e?.message)
    return Promise.reject(e)
  } finally {
    dispatch(updateActionState({ action: 'ordering', state: false }))
  }
})

export function initialOrderPayload(
  sourcePayload: BaseCreateOrderPayload,
): CreateOrderBody {
  const { symbol, price, amount, orderDirection, orderMode, feeRates } =
    sourcePayload

  if (
    !feeRates ||
    feeRates.taker < 0 ||
    Math.abs(feeRates.taker) < Math.abs(feeRates.maker)
  ) {
    throw new Error(`Invalid fees ${JSON.stringify(feeRates)}`)
  }

  if (isZero(amount)) {
    throw new Error(`Invalid amount ${amount}`)
  }

  // If the order mode is stop market, the price will be 0, but stop price must be greater than 0
  if (isZero(price) && isZero(sourcePayload.stopPrice)) {
    throw new Error(`Invalid price ${price}`)
  }

  if (
    sourcePayload.orderMode.name === OrderModeNames.StopLimit ||
    sourcePayload.orderMode.name === OrderModeNames.StopMarket
  ) {
    if (isZero(sourcePayload.stopPrice)) {
      throw new Error(`Invalid stop price ${sourcePayload.stopPrice}`)
    }
  }

  const quantity = w2e(
    sdk.closestPackableTransactionAmount(e2w(amount).toString()),
  )

  const orderPayload: CreateOrderBody = {
    source: 'WEB',
    direction: orderDirection,
    triggerDirection: orderDirection,
    type: orderMode.orderType,
    symbol,
    quantity,
    postOnly: false,
    hidden: false,
    price: Number(price),
    triggerOn: 0,
    fillOrKill: false,
    immediateOrCancel: false,
    signature: '',
    slotId: 0,
    nonce: 0,
    makerFeeRate: 0,
    takerFeeRate: 0,
  }

  return orderPayload
}

export async function fillSlotIdAndNonce(
  payload: CreateOrderBody,
  slotType: SlotType,
): Promise<CreateOrderBody> {
  const slot = await getSlot(slotType)
  if (!slot) {
    toastify.error('No slot available')
  }
  const { data } = slot
  return {
    ...payload,
    slotId: data.slotId,
    nonce: data.nonce,
  }
}

export function fillHidden(
  payload: CreateOrderBody,
  orderMode: OrderModeDetail,
): CreateOrderBody {
  const isIcebergOrder = orderMode.name === OrderModeNames.Iceberg
  return {
    ...payload,
    hidden: isIcebergOrder,
  }
}

async function fillSpotZkSignature(
  payload: CreateOrderBody,
  sourcePayload: SpotCreateOrderPayload,
  signer: sdk.JsonRpcSigner,
): Promise<CreateOrderBody> {
  const isIcebergOrder = sourcePayload.orderMode.name === OrderModeNames.Iceberg
  if (!sourcePayload.feeRates) {
    throw new Error('Cannot get fee rates')
  }
  const signedData = await signOrder(signer, {
    accountId: sourcePayload.l2AccountId,
    subAccountId: SUB_ACCOUNT_ID,
    slotId: payload.slotId,
    nonce: payload.nonce,
    baseTokenId: sourcePayload.baseTokenId,
    quoteTokenId: sourcePayload.quoteTokenId,
    price: e2w(payload.price).toString(),
    amount: e2w(payload.quantity).toString(),
    isSell: payload.direction === OrderDirection.SHORT,
    // In the iceberg order, the maker will be replaced by the taker
    feeRates: [
      isIcebergOrder
        ? sourcePayload.feeRates.taker
        : sourcePayload.feeRates.maker,
      sourcePayload.feeRates.taker,
    ],
  })

  return {
    ...payload,
    signature: signedData.tx.signature.signature,
  }
}

export function fillStopLimitOrder(
  payload: CreateOrderBody,
  orderMode: OrderModeDetail,
  stopPrice: Ether,
): CreateOrderBody {
  if (orderMode.name === OrderModeNames.StopLimit) {
    return {
      ...payload,
      triggerOn: Number(stopPrice),
    }
  }
  return payload
}

export function fillTriggerDirection(
  payload: CreateOrderBody,
  orderMode: OrderModeDetail,
): CreateOrderBody {
  const { orderbook } = store.getState()
  const { price } = orderbook.orderbook

  if (
    orderMode.name === OrderModeNames.StopLimit ||
    orderMode.name === OrderModeNames.StopMarket
  ) {
    return {
      ...payload,
      triggerDirection: getStopOrderTriggerDirection(
        payload.triggerOn,
        Number(price),
      ),
    }
  }
  return payload
}

export async function fillStopMarketOrder(
  payload: CreateOrderBody,
  orderMode: OrderModeDetail,
  stopPrice: Ether,
  quoteScale: number,
  tradeType: TradeType,
): Promise<CreateOrderBody> {
  if (orderMode.name === OrderModeNames.StopMarket) {
    const actionResult = await store.dispatch(getMarketPriceConfigAction())
    if (isFulfilled(actionResult)) {
      const marketPriceConfig = actionResult.payload
      const price = calculateMarketPriceForStopMarket(
        payload.direction,
        stopPrice,
        tradeType,
        quoteScale,
        marketPriceConfig,
      )

      return {
        ...payload,
        price: Number(price),
        triggerOn: Number(stopPrice),
      }
    } else {
      throw new Error('Cannot get market price config')
    }
  }
  return payload
}

export function convertFeeRateToFloat(
  payload: CreateOrderBody,
  sourcePayload: SpotCreateOrderPayload,
) {
  const { feeRates } = sourcePayload

  if (!feeRates) {
    throw new Error('Cannot get fee rates')
  }

  return {
    ...payload,
    makerFeeRate: Number(formatUnits(bn(feeRates.maker), 4)),
    takerFeeRate: Number(formatUnits(bn(feeRates.taker), 4)),
  }
}

export function fillOrderTIF(
  payload: CreateOrderBody,
  orderMode: OrderModeDetail,
  tifType: TifType,
) {
  // Market order and stop market order do not support TIF
  if (
    orderMode.name === OrderModeNames.Market ||
    orderMode.name === OrderModeNames.StopMarket
  ) {
    return {
      ...payload,
      immediateOrCancel: false,
      fillOrKill: false,
    }
  }

  return {
    ...payload,
    immediateOrCancel: tifType === TifType.IOC,
    fillOrKill: tifType === TifType.FOK,
  }
}

export function fillOrderPostOnly(
  payload: CreateOrderBody,
  orderMode: OrderModeDetail,
  postOnly: boolean,
) {
  // Market order and stop market order do not support postOnly
  if (
    orderMode.name === OrderModeNames.Market ||
    orderMode.name === OrderModeNames.StopMarket
  ) {
    return {
      ...payload,
      postOnly: false,
    }
  }
  return {
    ...payload,
    postOnly,
  }
}
